import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react"
import api from "../services/api"
import { apiPath } from "../config/paths"
import AdminLayout from "../components/AdminLayout"
import { useAdminFeedback } from "../context/AdminFeedbackContext"

const NdaFieldPositionPicker = lazy(() => import("../components/NdaFieldPositionPicker"))

const C = {
    navy: "#002B54",
    navyLight: "#163354",
    navyMid: "#1e4070",
    blue: "#1a6fc4",
    blueLight: "#3b8de0",
    bluePale: "#e8f0fb",
    bluePale2: "#dceaf8",
    white: "#ffffff",
    offWhite: "#f5f7fa",
    gray100: "#eef1f5",
    gray200: "#dde2ea",
    gray300: "#c3cad5",
    gray400: "#94a0b0",
    gray500: "#64748b",
    gray700: "#334155",
    green: "#15803d",
    greenBg: "#dcfce7",
    greenBorder: "#86efac",
    red: "#dc2626",
    redBg: "#fef2f2",
    redBorder: "#fca5a5",
    orange: "#F4811F",
    orangeBg: "#fff4eb",
}

export default function Settings() {
    const { confirm } = useAdminFeedback()
    const [email, setEmail] = useState("")
    const [status, setStatus] = useState(null)
    const [loading, setLoading] = useState(false)
    const [resetting, setResetting] = useState(false)
    const [message, setMessage] = useState(null)

    // NDA Template state
    const [ndaStatus, setNdaStatus] = useState(null)
    const [ndaLoading, setNdaLoading] = useState(true)
    const [ndaUploading, setNdaUploading] = useState(false)
    const [ndaMessage, setNdaMessage] = useState(null)
    const [dragOver, setDragOver] = useState(false)
    const [ndaViewerOpen, setNdaViewerOpen] = useState(false)
    const fileInputRef = useRef(null)

    // NDA field position config
    const [ndaFields, setNdaFields] = useState(null)
    const [ndaFieldsLoading, setNdaFieldsLoading] = useState(true)
    const [ndaFieldsSaving, setNdaFieldsSaving] = useState(false)
    const [ndaFieldsMessage, setNdaFieldsMessage] = useState(null)
    const [activeNdaField, setActiveNdaField] = useState("effectiveDate")
    const [ndaPickerReload, setNdaPickerReload] = useState(0)

    // Download Restriction state
    const [downloadRestriction, setDownloadRestriction] = useState(false)
    const [downloadRestrictionLoading, setDownloadRestrictionLoading] = useState(true)
    const [downloadRestrictionSaving, setDownloadRestrictionSaving] = useState(false)
    const [downloadRestrictionMessage, setDownloadRestrictionMessage] = useState(null)
    const [downloadRestrictionUpdatedAt, setDownloadRestrictionUpdatedAt] = useState(null)

    useEffect(() => {
        fetchNdaStatus()
        fetchNdaFieldPositions()
        fetchDownloadRestriction()
    }, [])

    const fetchNdaStatus = async () => {
        setNdaLoading(true)
        try {
            const res = await api.get("/admin/settings/nda-template")
            setNdaStatus(res.data)
        } catch {
            setNdaStatus(null)
        } finally {
            setNdaLoading(false)
        }
    }

    const fetchNdaFieldPositions = async () => {
        setNdaFieldsLoading(true)
        try {
            const res = await api.get("/admin/settings/nda-field-positions")
            setNdaFields(res.data.fields)
        } catch {
            setNdaFields(null)
        } finally {
            setNdaFieldsLoading(false)
        }
    }

    const saveNdaFieldPositions = async () => {
        if (!ndaFields) return
        setNdaFieldsSaving(true)
        setNdaFieldsMessage(null)
        try {
            const res = await api.put("/admin/settings/nda-field-positions", { fields: ndaFields })
            setNdaFields(res.data.fields)
            setNdaFieldsMessage({ type: "success", text: "NDA field positions saved." })
        } catch (err) {
            setNdaFieldsMessage({
                type: "error",
                text: err.response?.data?.error || "Failed to save NDA field positions.",
            })
        } finally {
            setNdaFieldsSaving(false)
        }
    }

    const updateNdaField = (fieldKey, prop, value) => {
        setNdaFields((prev) => ({
            ...prev,
            [fieldKey]: {
                ...prev[fieldKey],
                [prop]: value,
            },
        }))
    }

    const placeNdaField = useCallback((fieldKey, { page, x, y }) => {
        setNdaFields((prev) => ({
            ...prev,
            [fieldKey]: {
                ...prev[fieldKey],
                page,
                x,
                y,
            },
        }))
    }, [])

    const fetchDownloadRestriction = async () => {
        setDownloadRestrictionLoading(true)
        try {
            const res = await api.get("/admin/settings/download-restriction")
            setDownloadRestriction(res.data.enabled)
            setDownloadRestrictionUpdatedAt(res.data.updated_on)
        } catch {
            setDownloadRestriction(false)
        } finally {
            setDownloadRestrictionLoading(false)
        }
    }

    const toggleDownloadRestriction = async () => {
        const newValue = !downloadRestriction
        setDownloadRestrictionSaving(true)
        setDownloadRestrictionMessage(null)
        try {
            const res = await api.put("/admin/settings/download-restriction", { enabled: newValue })
            setDownloadRestriction(res.data.enabled)
            setDownloadRestrictionUpdatedAt(res.data.updated_on)
            setDownloadRestrictionMessage({
                type: "success",
                text: newValue
                    ? "Download restricted — prospects can no longer download documents."
                    : "Download restriction removed — all users can download documents."
            })
        } catch (err) {
            setDownloadRestrictionMessage({
                type: "error",
                text: err.response?.data?.error || "Failed to update setting"
            })
        } finally {
            setDownloadRestrictionSaving(false)
        }
    }

    const uploadNdaTemplate = async (file) => {
        if (!file) return
        if (file.type !== "application/pdf") {
            setNdaMessage({ type: "error", text: "Only PDF files are allowed." })
            return
        }
        if (file.size > 10 * 1024 * 1024) {
            setNdaMessage({ type: "error", text: "File size must be under 10 MB." })
            return
        }

        setNdaUploading(true)
        setNdaMessage(null)
        try {
            const formData = new FormData()
            formData.append("file", file)
            const res = await api.post("/admin/settings/nda-template", formData, {
                headers: { "Content-Type": "multipart/form-data" }
            })
            setNdaMessage({ type: "success", text: res.data.message })
            await fetchNdaStatus()
            setNdaPickerReload((k) => k + 1)
        } catch (err) {
            setNdaMessage({ type: "error", text: err.response?.data?.error || "Upload failed" })
        } finally {
            setNdaUploading(false)
            if (fileInputRef.current) fileInputRef.current.value = ""
        }
    }

    const downloadNdaTemplate = async () => {
        try {
            const res = await api.get("/admin/settings/nda-template/view?download=1", { responseType: "blob" })
            const url = URL.createObjectURL(res.data)
            const a = document.createElement("a")
            a.href = url
            a.download = ndaStatus?.originalName || "nda-template.pdf"
            document.body.appendChild(a)
            a.click()
            a.remove()
            setTimeout(() => URL.revokeObjectURL(url), 2000)
        } catch {
            setNdaMessage({ type: "error", text: "Failed to download NDA template." })
        }
    }

    const handleDrop = (e) => {
        e.preventDefault()
        setDragOver(false)
        const file = e.dataTransfer.files?.[0]
        if (file) uploadNdaTemplate(file)
    }

    const fetchStatus = async () => {
        if (!email.trim()) return
        setLoading(true)
        setMessage(null)
        setStatus(null)
        try {
            const res = await api.get(`/admin/otp-status?email=${encodeURIComponent(email.trim().toLowerCase())}`)
            setStatus(res.data)
        } catch (err) {
            setMessage({ type: "error", text: err.response?.data?.error || "Failed to fetch OTP status" })
        } finally {
            setLoading(false)
        }
    }

    const resetLimits = async () => {
        if (!email.trim()) return
        const ok = await confirm(`Reset OTP limits for ${email}? This will unblock the user immediately.`)
        if (!ok) return
        setResetting(true)
        setMessage(null)
        try {
            const res = await api.post("/admin/reset-otp-limit", { email: email.trim().toLowerCase() })
            setMessage({ type: "success", text: res.data.message })
            setStatus(null)
        } catch (err) {
            setMessage({ type: "error", text: err.response?.data?.error || "Failed to reset OTP limits" })
        } finally {
            setResetting(false)
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === "Enter") fetchStatus()
    }

    const formatFileSize = (bytes) => {
        if (!bytes) return "—"
        if (bytes < 1024) return `${bytes} B`
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
    }

    return (
        <AdminLayout title="Settings" description="Manage NDA templates, OTP rate limits, and access controls.">
            <div style={{ width: "100%", maxWidth: "none", margin: 0, fontFamily: "'DM Sans', 'Segoe UI', sans-serif", display: "flex", flexDirection: "column", gap: "28px" }}>

                {/* ═══════ NDA template & field mapping ═══════ */}
                <div style={{
                    background: C.white,
                    border: `1px solid ${C.gray200}`,
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 2px 16px rgba(12,35,64,0.07)",
                }}>
                    <div style={{ padding: "22px 24px 18px", borderBottom: `1px solid ${C.gray100}` }}>
                        <div style={{ fontSize: "18px", fontWeight: "800", color: C.navy }}>NDA template & field mapping</div>
                        <div style={{ fontSize: "13px", color: C.gray500, marginTop: "4px", lineHeight: 1.5 }}>
                            The PDF prospects sign, and where their company name and the signing date are printed on it.
                        </div>
                        {ndaStatus?.exists && (ndaStatus.uploadedByName || ndaStatus.lastModified) && (
                            <div style={{ marginTop: "10px", fontSize: "12px", color: C.gray400 }}>
                                {ndaStatus.uploadedByName ? <>Last published by <strong style={{ color: C.navy }}>{ndaStatus.uploadedByName}</strong></> : "Last published"}
                                {ndaStatus.lastModified && <> · {new Date(ndaStatus.lastModified).toLocaleString("en-GB", {
                                    day: "2-digit", month: "short", year: "numeric",
                                    hour: "2-digit", minute: "2-digit",
                                })}</>}
                            </div>
                        )}
                    </div>

                    <div style={{ padding: "22px 24px", display: "flex", flexDirection: "column", gap: "28px" }}>
                        {/* TEMPLATE */}
                        <section>
                            <div style={{ fontSize: "11px", fontWeight: "800", letterSpacing: "0.08em", textTransform: "uppercase", color: C.gray400, marginBottom: "10px" }}>
                                Template
                            </div>
                            <div style={{
                                fontSize: "12px", color: C.gray500, marginBottom: "12px",
                                padding: "10px 12px", borderRadius: "8px", background: C.bluePale, border: `1px solid ${C.bluePale2}`,
                            }}>
                                Signed NDAs stay pinned to the version they were signed on.
                            </div>

                            {ndaLoading ? (
                                <div style={{ textAlign: "center", padding: "20px", color: C.gray400, fontSize: "13px" }}>
                                    Checking template status...
                                </div>
                            ) : ndaStatus?.exists ? (
                                <div style={{
                                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px",
                                    flexWrap: "wrap",
                                    border: `1px solid ${C.gray200}`, borderRadius: "12px", padding: "16px 18px",
                                    background: C.offWhite,
                                }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "14px", minWidth: 0 }}>
                                        <div style={{
                                            width: 44, height: 44, borderRadius: 10, background: "#fff",
                                            border: `1px solid ${C.gray200}`, display: "grid", placeItems: "center", flexShrink: 0,
                                            color: C.blue, fontSize: 11, fontWeight: 800,
                                        }}>PDF</div>
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                                <span style={{ fontSize: 14, fontWeight: 800, color: C.navy }}>
                                                    {ndaStatus.originalName || "Darwinbox-Prospect-NDA.pdf"}
                                                </span>
                                                {ndaStatus.version != null && (
                                                    <span style={{ fontSize: 12, fontWeight: 700, color: C.gray500 }}>V{ndaStatus.version}</span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: 12, color: C.gray500, marginTop: 4 }}>
                                                {ndaStatus.pageCount ? `${ndaStatus.pageCount} page${ndaStatus.pageCount === 1 ? "" : "s"} · ` : ""}
                                                {formatFileSize(ndaStatus.size)}
                                                {ndaStatus.uploadedByName ? ` · published by ${ndaStatus.uploadedByName}` : ""}
                                                {ndaStatus.lastModified ? ` on ${new Date(ndaStatus.lastModified).toLocaleString("en-GB", {
                                                    day: "2-digit", month: "short", year: "numeric",
                                                    hour: "2-digit", minute: "2-digit",
                                                })}` : ""}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                        <button type="button" onClick={() => setNdaViewerOpen(true)} style={{
                                            padding: "8px 14px", borderRadius: 8, border: `1px solid ${C.gray200}`,
                                            background: "#fff", color: C.navy, fontSize: 12, fontWeight: 700, cursor: "pointer",
                                        }}>View</button>
                                        <button type="button" onClick={downloadNdaTemplate} style={{
                                            padding: "8px 14px", borderRadius: 8, border: `1px solid ${C.gray200}`,
                                            background: "#fff", color: C.navy, fontSize: 12, fontWeight: 700, cursor: "pointer",
                                        }}>Download</button>
                                        <button
                                            type="button"
                                            onClick={() => !ndaUploading && fileInputRef.current?.click()}
                                            disabled={ndaUploading}
                                            style={{
                                                padding: "8px 14px", borderRadius: 8, border: "none",
                                                background: `linear-gradient(135deg, ${C.navy}, ${C.navyMid})`,
                                                color: "#fff", fontSize: 12, fontWeight: 700,
                                                cursor: ndaUploading ? "wait" : "pointer",
                                                opacity: ndaUploading ? 0.7 : 1,
                                            }}
                                        >{ndaUploading ? "Uploading…" : "Replace template"}</button>
                                    </div>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="application/pdf"
                                        style={{ display: "none" }}
                                        onChange={(e) => uploadNdaTemplate(e.target.files?.[0])}
                                    />
                                </div>
                            ) : (
                                <div
                                    onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                                    onDragLeave={() => setDragOver(false)}
                                    onDrop={handleDrop}
                                    onClick={() => !ndaUploading && fileInputRef.current?.click()}
                                    style={{
                                        border: `2px dashed ${dragOver ? C.blue : C.gray200}`,
                                        borderRadius: "12px",
                                        padding: "36px 24px",
                                        textAlign: "center",
                                        cursor: ndaUploading ? "wait" : "pointer",
                                        background: dragOver ? C.bluePale : C.offWhite,
                                    }}
                                >
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="application/pdf"
                                        style={{ display: "none" }}
                                        onChange={(e) => uploadNdaTemplate(e.target.files?.[0])}
                                    />
                                    <div style={{ fontSize: "13px", fontWeight: "700", color: C.navy, marginBottom: "4px" }}>
                                        {ndaUploading ? "Uploading…" : "Upload NDA template PDF"}
                                    </div>
                                    <div style={{ fontSize: "11px", color: C.gray400 }}>
                                        Drag & drop a PDF here, or click to browse — max 10 MB
                                    </div>
                                </div>
                            )}

                            {ndaMessage && (
                                <div style={{
                                    marginTop: 12, padding: "12px 16px", borderRadius: 10,
                                    background: ndaMessage.type === "success" ? C.greenBg : C.redBg,
                                    border: `1px solid ${ndaMessage.type === "success" ? C.greenBorder : C.redBorder}`,
                                    fontSize: 13, fontWeight: 600,
                                    color: ndaMessage.type === "success" ? C.green : C.red,
                                }}>
                                    {ndaMessage.text}
                                </div>
                            )}
                        </section>

                        {/* FIELD PLACEMENT */}
                        <section>
                            <div style={{ fontSize: "11px", fontWeight: "800", letterSpacing: "0.08em", textTransform: "uppercase", color: C.gray400, marginBottom: "10px" }}>
                                Field placement
                            </div>
                            {ndaFieldsLoading ? (
                                <div style={{ textAlign: "center", padding: "20px", color: C.gray400, fontSize: "13px" }}>
                                    Loading field positions...
                                </div>
                            ) : !ndaStatus?.exists ? (
                                <div style={{ fontSize: 13, color: C.gray500, padding: "16px", background: C.offWhite, borderRadius: 10, border: `1px solid ${C.gray200}` }}>
                                    Upload a template to configure where the signing date and company name are printed.
                                </div>
                            ) : ndaFields ? (
                                <Suspense fallback={
                                    <div style={{ padding: 20, textAlign: "center", color: C.gray400, fontSize: 13 }}>
                                        Loading PDF picker…
                                    </div>
                                }>
                                    <NdaFieldPositionPicker
                                        fields={ndaFields}
                                        activeField={activeNdaField}
                                        onActiveFieldChange={setActiveNdaField}
                                        onPlaceField={placeNdaField}
                                        onUpdateField={updateNdaField}
                                        onSave={saveNdaFieldPositions}
                                        saving={ndaFieldsSaving}
                                        saveMessage={ndaFieldsMessage}
                                        reloadToken={ndaPickerReload}
                                    />
                                </Suspense>
                            ) : (
                                <div style={{ fontSize: "13px", color: C.gray500 }}>
                                    Could not load NDA field positions.
                                </div>
                            )}
                        </section>
                    </div>
                </div>

                {/* ═══════ Download Restriction Card ═══════ */}
                <div style={{
                    background: C.white,
                    border: `1px solid ${C.gray200}`,
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 2px 16px rgba(12,35,64,0.07)",
                }}>
                    <div style={{
                        padding: "20px 24px",
                        borderBottom: `1px solid ${C.gray100}`,
                        background: C.offWhite,
                    }}>
                        <div style={{ fontSize: "15px", fontWeight: "800", color: C.navy }}>Prospect Download Restriction</div>
                        <div style={{ fontSize: "12px", color: C.gray400, marginTop: "2px" }}>
                            Control whether prospects can download documents from the Trust Center
                        </div>
                    </div>

                    <div style={{ padding: "24px" }}>
                        {downloadRestrictionLoading ? (
                            <div style={{ textAlign: "center", padding: "20px", color: C.gray400, fontSize: "13px" }}>
                                Loading setting...
                            </div>
                        ) : (
                            <>
                                <div style={{
                                    display: "flex", gap: "10px", alignItems: "flex-start",
                                    background: C.bluePale, border: `1px solid ${C.bluePale2}`,
                                    borderRadius: "10px", padding: "14px 16px", marginBottom: "20px",
                                }}>
                                    <span style={{ fontSize: "14px", flexShrink: 0, marginTop: "1px" }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10" />
                                            <path d="M12 16v-4" />
                                            <path d="M12 8h.01" />
                                        </svg>
                                    </span>
                                    <div style={{ fontSize: "12px", color: C.navy, lineHeight: "1.6" }}>
                                        When enabled, <strong>prospects</strong> will not see the Download button on documents.
                                        Only <strong>existing customers</strong> (verified via Darwinbox) and <strong>admins</strong> will be able to download.
                                        Prospects can still <strong>view</strong> documents they have access to.
                                    </div>
                                </div>

                                <div style={{
                                    display: "flex", alignItems: "center", justifyContent: "space-between",
                                    background: downloadRestriction ? C.orangeBg : C.offWhite,
                                    border: `1px solid ${downloadRestriction ? "#fed7aa" : C.gray200}`,
                                    borderRadius: "12px", padding: "18px 20px",
                                    marginBottom: "16px",
                                }}>
                                    <div>
                                        <div style={{ fontSize: "14px", fontWeight: "700", color: C.navy, marginBottom: "4px" }}>
                                            {downloadRestriction ? "Restriction Active" : "Restriction Off"}
                                        </div>
                                        <div style={{ fontSize: "12px", color: C.gray500 }}>
                                            {downloadRestriction
                                                ? "Prospects cannot download documents"
                                                : "All authorized users can download documents"
                                            }
                                        </div>
                                        {downloadRestrictionUpdatedAt && (
                                            <div style={{ fontSize: "11px", color: C.gray400, marginTop: "6px" }}>
                                                Last updated: {new Date(downloadRestrictionUpdatedAt).toLocaleString("en-GB", {
                                                    day: "2-digit", month: "short", year: "numeric",
                                                    hour: "2-digit", minute: "2-digit"
                                                })}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={toggleDownloadRestriction}
                                        disabled={downloadRestrictionSaving}
                                        style={{
                                            position: "relative",
                                            width: "52px", height: "28px",
                                            borderRadius: "14px",
                                            border: "none",
                                            cursor: downloadRestrictionSaving ? "wait" : "pointer",
                                            background: downloadRestriction
                                                ? `linear-gradient(135deg, ${C.orange}, #ea580c)`
                                                : C.gray300,
                                            transition: "background 0.25s",
                                            flexShrink: 0,
                                            opacity: downloadRestrictionSaving ? 0.6 : 1,
                                        }}
                                    >
                                        <div style={{
                                            position: "absolute",
                                            top: "3px",
                                            left: downloadRestriction ? "26px" : "3px",
                                            width: "22px", height: "22px",
                                            borderRadius: "50%",
                                            background: C.white,
                                            boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                                            transition: "left 0.25s",
                                        }} />
                                    </button>
                                </div>

                                {downloadRestrictionMessage && (
                                    <div style={{
                                        display: "flex", alignItems: "center", gap: "8px",
                                        padding: "12px 16px", borderRadius: "10px",
                                        background: downloadRestrictionMessage.type === "success" ? C.greenBg : C.redBg,
                                        border: `1px solid ${downloadRestrictionMessage.type === "success" ? C.greenBorder : C.redBorder}`,
                                        fontSize: "13px", fontWeight: "600",
                                        color: downloadRestrictionMessage.type === "success" ? C.green : C.red,
                                    }}>
                                        <span>{downloadRestrictionMessage.type === "success" ? "✓" : "!"}</span>
                                        {downloadRestrictionMessage.text}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* ═══════ OTP Rate Limit Card ═══════ */}
                <div style={{
                    background: C.white,
                    border: `1px solid ${C.gray200}`,
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 2px 16px rgba(12,35,64,0.07)",
                }}>
                    {/* Card Header */}
                    <div style={{
                        padding: "20px 24px",
                        borderBottom: `1px solid ${C.gray100}`,
                        background: C.offWhite,
                    }}>
                        <div style={{ fontSize: "15px", fontWeight: "800", color: C.navy }}>OTP Rate Limit Reset</div>
                        <div style={{ fontSize: "12px", color: C.gray400, marginTop: "2px" }}>
                            Look up a user's OTP status and reset their limits if blocked
                        </div>
                    </div>

                    {/* Card Body */}
                    <div style={{ padding: "24px" }}>
                        {/* Info Banner */}
                        <div style={{
                            display: "flex", gap: "10px", alignItems: "flex-start",
                            background: C.bluePale, border: `1px solid ${C.bluePale2}`,
                            borderRadius: "10px", padding: "14px 16px", marginBottom: "20px",
                        }}>
                            <span style={{ fontSize: "14px", flexShrink: 0, marginTop: "1px" }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M12 16v-4" />
                                    <path d="M12 8h.01" />
                                </svg>
                            </span>
                            <div style={{ fontSize: "12px", color: C.navy, lineHeight: "1.6" }}>
                                <strong>Rate limits:</strong> Users can request OTP up to <strong>5 times</strong> and
                                enter a wrong OTP up to <strong>3 times</strong>. After exceeding either limit,
                                both sending and verifying are blocked for <strong>24 hours</strong>.
                            </div>
                        </div>

                        {/* Email Input */}
                        <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
                            <input
                                type="email"
                                placeholder="Enter user email address..."
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onKeyDown={handleKeyDown}
                                style={{
                                    flex: 1, padding: "12px 16px",
                                    borderRadius: "10px", border: `1.5px solid ${C.gray200}`,
                                    fontSize: "14px", color: C.navy, outline: "none",
                                    transition: "border-color 0.2s, box-shadow 0.2s",
                                    fontWeight: "500",
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = C.blue
                                    e.target.style.boxShadow = `0 0 0 3px ${C.bluePale}`
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = C.gray200
                                    e.target.style.boxShadow = "none"
                                }}
                            />
                            <button
                                onClick={fetchStatus}
                                disabled={loading || !email.trim()}
                                style={{
                                    padding: "12px 24px", borderRadius: "10px",
                                    background: email.trim()
                                        ? `linear-gradient(135deg, ${C.navy}, ${C.navyMid})`
                                        : C.gray200,
                                    color: email.trim() ? C.white : C.gray400,
                                    border: "none", fontSize: "13px", fontWeight: "700",
                                    cursor: email.trim() ? "pointer" : "not-allowed",
                                    transition: "all 0.18s",
                                    whiteSpace: "nowrap",
                                    opacity: loading ? 0.7 : 1,
                                }}
                            >
                                {loading ? "Checking..." : "Check Status"}
                            </button>
                        </div>

                        {/* Status Display */}
                        {status && (
                            <div style={{
                                background: status.isBlocked ? C.redBg : C.offWhite,
                                border: `1px solid ${status.isBlocked ? C.redBorder : C.gray200}`,
                                borderRadius: "12px", padding: "20px",
                                marginBottom: "16px",
                            }}>
                                <div style={{
                                    display: "flex", alignItems: "center", justifyContent: "space-between",
                                    marginBottom: "16px",
                                }}>
                                    <div style={{ fontSize: "13px", fontWeight: "700", color: C.navy }}>
                                        {status.email}
                                    </div>
                                    <div style={{
                                        display: "inline-flex", alignItems: "center", gap: "5px",
                                        padding: "4px 12px", borderRadius: "20px",
                                        fontSize: "10px", fontWeight: "800", textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                        background: status.isBlocked ? C.redBg : C.greenBg,
                                        color: status.isBlocked ? C.red : C.green,
                                        border: `1px solid ${status.isBlocked ? C.redBorder : C.greenBorder}`,
                                    }}>
                                        <div style={{
                                            width: "6px", height: "6px", borderRadius: "50%",
                                            background: status.isBlocked ? C.red : C.green,
                                        }} />
                                        {status.isBlocked ? "Blocked" : "Active"}
                                    </div>
                                </div>

                                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px" }}>
                                    <StatBox
                                        label="OTP Sends"
                                        value={`${status.sendCount} / 5`}
                                        warn={status.sendCount >= 4}
                                        danger={status.sendCount >= 5}
                                    />
                                    <StatBox
                                        label="Failed Verifies"
                                        value={`${status.failedVerifyCount} / 3`}
                                        warn={status.failedVerifyCount >= 2}
                                        danger={status.failedVerifyCount >= 3}
                                    />
                                    <StatBox
                                        label="Blocked Until"
                                        value={status.blockedUntil
                                            ? new Date(status.blockedUntil).toLocaleString("en-GB", {
                                                day: "2-digit", month: "short", year: "numeric",
                                                hour: "2-digit", minute: "2-digit"
                                            })
                                            : "Not blocked"
                                        }
                                        danger={!!status.isBlocked}
                                    />
                                </div>

                                {/* Reset Button */}
                                {(status.sendCount > 0 || status.failedVerifyCount > 0 || status.isBlocked) && (
                                    <button
                                        onClick={resetLimits}
                                        disabled={resetting}
                                        style={{
                                            marginTop: "16px", width: "100%",
                                            padding: "12px", borderRadius: "10px",
                                            background: status.isBlocked
                                                ? `linear-gradient(135deg, ${C.red}, #b91c1c)`
                                                : `linear-gradient(135deg, ${C.orange}, #ea580c)`,
                                            color: C.white, border: "none",
                                            fontSize: "13px", fontWeight: "700",
                                            cursor: "pointer", transition: "all 0.18s",
                                            opacity: resetting ? 0.7 : 1,
                                        }}
                                    >
                                        {resetting ? "Resetting..." : status.isBlocked ? "Unblock & Reset All Limits" : "Reset Limits"}
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Message Banner */}
                        {message && (
                            <div style={{
                                display: "flex", alignItems: "center", gap: "8px",
                                padding: "12px 16px", borderRadius: "10px",
                                background: message.type === "success" ? C.greenBg : C.redBg,
                                border: `1px solid ${message.type === "success" ? C.greenBorder : C.redBorder}`,
                                fontSize: "13px", fontWeight: "600",
                                color: message.type === "success" ? C.green : C.red,
                            }}>
                                <span>{message.type === "success" ? "✓" : "!"}</span>
                                {message.text}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {ndaViewerOpen && (
                <div style={{
                    position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 9999,
                    display: "flex", alignItems: "center", justifyContent: "center", padding: "20px",
                }}>
                    <div style={{
                        background: C.white, borderRadius: "16px", width: "95vw", maxWidth: "1200px",
                        height: "90vh", display: "flex", flexDirection: "column", overflow: "hidden",
                    }}>
                        <div style={{
                            padding: "16px 24px", borderBottom: `1px solid ${C.gray100}`,
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                        }}>
                            <span style={{ fontWeight: 700, color: C.navy }}>NDA Template</span>
                            <button
                                type="button"
                                onClick={() => setNdaViewerOpen(false)}
                                style={{
                                    border: "none", background: "none", fontSize: "24px",
                                    cursor: "pointer", color: C.gray500, lineHeight: 1,
                                }}
                                aria-label="Close viewer"
                            >
                                &times;
                            </button>
                        </div>
                        <div style={{ flex: 1, background: "#f1f5f9" }}>
                            <iframe
                                src={`${apiPath("/admin/settings/nda-template/view")}#toolbar=0`}
                                style={{ width: "100%", height: "100%", border: "none" }}
                                title="NDA Template Viewer"
                            />
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    )
}

function StatBox({ label, value, warn, danger }) {
    const bg = danger ? C.redBg : warn ? C.orangeBg : C.white
    const borderColor = danger ? C.redBorder : warn ? "#fed7aa" : C.gray200
    const valueColor = danger ? C.red : warn ? C.orange : C.navy

    return (
        <div style={{
            background: bg, border: `1px solid ${borderColor}`,
            borderRadius: "10px", padding: "12px 14px", textAlign: "center",
        }}>
            <div style={{ fontSize: "10px", fontWeight: "700", color: C.gray400, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                {label}
            </div>
            <div style={{ fontSize: "15px", fontWeight: "800", color: valueColor }}>
                {value}
            </div>
        </div>
    )
}
